import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { AlertOctagon } from 'lucide-react'
import { useSession } from '../auth/useSession'
import { pollWhileVisible } from '../lib/pollWhileVisible'
import { EmptyState } from '../cc/EmptyState'
import { FilterChips } from '../cc/FilterChips'
import { NetradyneCameraButton } from '../cc/NetradyneCameraButton'
import { KpiCard } from '../cc/KpiCard'
import { KpiStrip } from '../cc/KpiStrip'
import { Panel } from '../cc/Panel'
import { EYEBROW } from '../cc/tokens'
import type { VehicleStatus } from '../cc/VehicleCard'
import { ago, clock } from '../lib/format'
import {
  isAlertVisible, isSustainedAlertStillOpen, isSustainedConditionKind, useSustainedAlertDeadSet,
} from '../lib/alertVisibility'
import { defaultSurfaceFor, fleetPills, pathFor, surfacePills, type FleetId } from '../cc/fleetNav'
import { TopBar } from '../cc/TopBar'
import { StarMap } from './StarMap'
import { StarBreakdownForm } from './StarBreakdownForm'
import {
  actOnStarBreakdown,
  dismissStarMoving,
  fetchStarAlerts,
  fetchStarBreakdownCodes,
  fetchStarBreakdownReport,
  fetchStarBreakdowns,
  fetchStarHubs,
  fetchStarLive,
  fetchStarRoads,
  fetchStarSummary,
  type StarAlert,
  type StarBreakdown,
  type StarBreakdownCode,
  type StarBreakdownReportKind,
  type StarBreakdownReportRow,
  type StarHub,
  type StarRoad,
  type StarSummary,
  type StarVehicle,
} from './useStarApi'

function sessionDisplayName(session: ReturnType<typeof useSession>): string | undefined {
  return session.status === 'signed-in' ? session.session.name || session.session.email : undefined
}

const ALERT_TONE: Record<string, string> = {
  CRITICAL: '#9F1239',
  WARN: '#B45309',
  INFO: '#64748B',
}

// Same thresholds console/src/global/useGlobalApi.ts already uses to classify STAR
// vehicles on the unified all-fleet map (PROPEL reports every ~5s, so a full minute of
// silence is real; 2 kph clears GPS-jitter-only "speed" reads on a parked truck) --
// reused here rather than re-derived, so this shell's own OFFLINE chip and the global
// map's "Offline" bucket never disagree about the same vehicle.
const STAR_STALE_MS = 60_000
// Known, accepted divergence from the backend's own operator-configurable
// STAR_STOP_SPEED_KPH tunable (serving/app.py's "STAR_STOP_SPEED_KPH" effective-config
// key, star_rules.STAR_STOP_SPEED_KPH_DEFAULT = 3.0) -- this frontend threshold is a
// fixed constant, not fetched from that config, so an operator who tunes the backend
// value will see this chip's idle/moving split (and, since the sustained-condition
// exemption's isSustainedAlertStillOpen call below reads starStatus() too, whether an
// EXCESSIVE_IDLING/UNSCHEDULED_STOP tile is judged "still open") disagree with the
// backend's own firing threshold by up to that difference. Flagged (adversarial
// review MED), not silently assumed in sync -- syncing it would mean plumbing that
// tunable to the frontend, a separate change.
const STAR_MOVING_KPH = 2

function starStatus(v: StarVehicle, isDown: boolean, now: number): VehicleStatus {
  if (isDown) return 'alert'
  if (now - new Date(v.polled_ts).getTime() > STAR_STALE_MS) return 'offline'
  if (v.charging_status === 1) return 'loading'
  return (v.speed ?? 0) > STAR_MOVING_KPH ? 'moving' : 'idle'
}

const FLEET_FILTERS = [
  { id: 'all', label: 'ALL' },
  { id: 'moving', label: 'MOVING' },
  { id: 'idle', label: 'IDLE' },
  { id: 'loading', label: 'CHARGING' },
  { id: 'alert', label: 'DOWN' },
  { id: 'offline', label: 'OFFLINE' },
]

/** Netradyne's own numeric alert id -- only present when sync_star_netradyne.py wrote
 *  this row (detail.source === 'netradyne', same shape as Ultratech's alert.detail; see
 *  that script's module docstring). STAR's own rule-derived alerts (speed, idling, etc.)
 *  have no Netradyne footage to request. */
function starNetradyneAlertId(a: StarAlert): number | null {
  const detail = a.detail as { source?: string; netradyne_alert_id?: string | number } | null
  if (detail?.source !== 'netradyne' || detail.netradyne_alert_id == null) return null
  const id = Number(detail.netradyne_alert_id)
  return Number.isFinite(id) ? id : null
}

function StarVehicleTile({ v, selected, onSelect, openBreakdown, onReportBreakdown }: {
  v: StarVehicle; selected: boolean; onSelect: () => void
  openBreakdown: StarBreakdown | null; onReportBreakdown: () => void
}): JSX.Element {
  const hasFix = v.latitude != null && v.longitude != null
  return (
    <div
      role="button"
      tabIndex={hasFix ? 0 : -1}
      onClick={hasFix ? onSelect : undefined}
      onKeyDown={(e) => { if (hasFix && (e.key === 'Enter' || e.key === ' ')) onSelect() }}
      title={hasFix ? 'Show on map' : 'No GPS fix yet -- nothing to show on the map'}
      className={
        'rounded-[12px] border p-3 text-left transition-colors '
        + (hasFix ? 'cursor-pointer hover:border-[#0369A1]/40 hover:bg-[#0369A1]/[0.03] ' : 'cursor-default ')
        + (selected ? 'border-[#0369A1] bg-[#0369A1]/[0.05]' : 'border-[rgba(15,23,42,0.08)] bg-white')
      }
    >
      <div className="flex items-center gap-2">
        <span className="font-mono text-[13px] font-bold tabular-nums text-[#0F172A]">{v.vehicle_id}</span>
        {openBreakdown && (
          <span className="flex items-center gap-1 rounded-full bg-[rgba(244,63,94,0.10)] px-1.5 py-0.5 text-[10px] font-semibold text-[#9F1239]">
            <AlertOctagon size={10} /> down
          </span>
        )}
        <span className="ml-auto text-[11px] text-[#94A3B8]" onClick={(e) => e.stopPropagation()}>
          <NetradyneCameraButton site="star_cement" vehicleNumber={v.vehicle_id} />
        </span>
        <span className="text-[11px] text-[#94A3B8]">
          {new Date(v.polled_ts).toLocaleTimeString('en-IN', { hour12: false, timeZone: 'Asia/Kolkata' })}
        </span>
      </div>
      <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-[#64748B]">
        <span>SOC <b className="font-mono tabular-nums text-[#0F172A]">{v.soc != null ? `${v.soc.toFixed(0)}%` : '—'}</b></span>
        <span>Speed <b className="font-mono tabular-nums text-[#0F172A]">{v.speed != null ? v.speed.toFixed(0) : '—'}</b> km/h</span>
        <span>SOH <b className="font-mono tabular-nums text-[#0F172A]">{v.soh != null ? `${v.soh.toFixed(0)}%` : '—'}</b></span>
        <span>Ignition <b className="font-mono tabular-nums text-[#0F172A]">{v.ignition_status === 1 ? 'On' : v.ignition_status === 0 ? 'Off' : '—'}</b></span>
      </div>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onReportBreakdown() }}
        className={
          'mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border px-2 py-1.5 text-[11px] font-semibold transition-colors '
          + (openBreakdown
            ? 'border-[rgba(244,63,94,0.30)] bg-[rgba(244,63,94,0.08)] text-[#9F1239] hover:bg-[rgba(244,63,94,0.14)]'
            : 'border-[rgba(15,23,42,0.10)] text-[#64748B] hover:border-[rgba(244,63,94,0.30)] hover:text-[#9F1239]')
        }
      >
        <AlertOctagon size={11} />
        {openBreakdown ? 'Update breakdown' : 'Mark breakdown'}
      </button>
    </div>
  )
}

function LiveView(): JSX.Element {
  const [vehicles, setVehicles] = useState<StarVehicle[]>([])
  const [summary, setSummary] = useState<StarSummary | null>(null)
  const [alerts, setAlerts] = useState<StarAlert[]>([])
  const [hubs, setHubs] = useState<StarHub[]>([])
  const [roads, setRoads] = useState<StarRoad[]>([])
  const [breakdowns, setBreakdowns] = useState<StarBreakdown[]>([])
  const [breakdownCodes, setBreakdownCodes] = useState<StarBreakdownCode[]>([])
  const [breakdownFormFor, setBreakdownFormFor] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [focusVehicleId, setFocusVehicleId] = useState<string | null>(null)
  const [filter, setFilter] = useState('all')
  // One shared clock for every starStatus() call this render -- the chip counts (inline
  // in JSX) and the filteredVehicles memo below must agree on "now", or a vehicle that
  // crosses the OFFLINE threshold between renders can show up offline in one and not the
  // other until the next 10s poll gives filteredVehicles a reason to recompute.
  const [now, setNow] = useState(() => Date.now())
  const sustainedAlertDeadSet = useSustainedAlertDeadSet()
  const mapPanelRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])

  const downSet = useMemo(() => new Set(breakdowns.map((b) => b.vehicle_id)), [breakdowns])
  // Live-tile TTL (.claude/work/SPEC.md): "Recent alerts" is a live panel an operator
  // watches in real time, not the Reports "Alerts -- last 200" table (AlertsView, below)
  // -- a row must stop showing 5 minutes after its own `ts`, independent of the 10s poll
  // that fetched it. Reuses the same `now` tick filteredVehicles already depends on, so
  // this introduces no new timer.
  // Sustained-condition kinds (STAR's own strings: IDLING/UNSCHEDULED_STOP) are exempted
  // from the ts TTL entirely -- see isSustainedAlertStillOpen's own comment for the
  // staleness gate/excluded-state gate/resurrection latch/max-age backstop this
  // composes.
  //
  // Round 4 HIGH-1: `isLive` is computed DIRECTLY from `polled_ts` freshness below,
  // never by deriving it from `starStatus()`. `starStatus()`'s `isDown` branch returns
  // 'alert' BEFORE it ever checks `polled_ts` -- so an open-breakdown truck with HOURS-
  // stale telemetry still read as `isLive: true` (since 'alert' !== 'offline') AND
  // `isExcludedState: true` (since 'alert' was in the exclusion check), which latched
  // the alertId dead forever off a read that was never trustworthy. Every other signal
  // below (moving/charging/down/ignition) is likewise read straight off the raw
  // fields, not through that same composite, for the identical reason -- `starStatus`
  // is still exactly right for `filteredVehicles` below (a FLEET-tab display concern),
  // just not for a correctness gate that controls a permanent latch.
  const visibleAlerts = useMemo(
    () => alerts.filter((a) => {
      if (!isSustainedConditionKind(a.kind)) return isAlertVisible(new Date(a.ts).getTime(), now)
      const v = vehicles.find((x) => x.vehicle_id === a.vehicle_id)
      const fresh = v != null && now - new Date(v.polled_ts).getTime() <= STAR_STALE_MS
      const moving = v != null && (v.speed ?? 0) > STAR_MOVING_KPH
      // Round 4 MED: matches the producer's own semantics (serving/star_rules.py:
      // "any other non-None value is treated as charging") -- `=== 1` would miss a
      // vendor value of e.g. `2`, which the backend itself treats as charging.
      const charging = v != null && v.charging_status != null && v.charging_status !== 0
      const isDown = v != null && downSet.has(v.vehicle_id)
      // Mirrors serving/star_rules.py::classify_stop's own mutual exclusivity exactly:
      // IDLING only holds while ignition is ON (ignition off means the backend would
      // route any new alert to UNSCHEDULED_STOP instead, not re-fire this one);
      // UNSCHEDULED_STOP only holds while ignition is OFF/unknown (ignition turning on
      // means the reverse).
      const ignitionFlipped = v != null && (
        a.kind === 'IDLING' ? v.ignition_status !== 1 : v.ignition_status === 1
      )
      return isSustainedAlertStillOpen({
        alertId: a.alert_id,
        ts: new Date(a.ts).getTime(),
        now,
        isLive: fresh,
        isMoving: moving,
        isExcludedState: charging || isDown || ignitionFlipped,
      }, sustainedAlertDeadSet)
    }),
    [alerts, now, vehicles, downSet, sustainedAlertDeadSet],
  )
  const filteredVehicles = useMemo(
    () => filter === 'all' ? vehicles : vehicles.filter((v) => starStatus(v, downSet.has(v.vehicle_id), now) === filter),
    [vehicles, filter, downSet, now],
  )

  const selectVehicle = useCallback((id: string) => {
    setFocusVehicleId(id)
    // A tile click coming from the vehicle list (below the fold on a short viewport)
    // should bring the map into view, not just silently pan a map the user can't see.
    mapPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [])

  // Guards against the 10s interval below firing a fresh round of 4 parallel fetches on
  // top of a round still in flight -- see BusOpsShell.tsx's own refreshInFlightRef for
  // the incident this class of bug caused there.
  const refreshInFlightRef = useRef(false)

  const refresh = useCallback(async () => {
    if (refreshInFlightRef.current) return
    refreshInFlightRef.current = true
    // All 4 fire immediately, same as the single Promise.all this replaces (the overlap
    // guard's own call-count test depends on that) -- only the awaiting/error-handling is
    // split into isolated groups, so a department without the alerts/breakdown_reports
    // grant 403ing the latter two can't blank the vehicle list/map that resolved fine.
    // Mirrors NagpurBusOpsShell's refresh().
    const livePromise = fetchStarLive()
    const summaryPromise = fetchStarSummary()
    const alertsPromise = fetchStarAlerts(undefined, 20)
    const breakdownsPromise = fetchStarBreakdowns(true)
    try {
      const [v, s] = await Promise.all([livePromise, summaryPromise])
      setVehicles(v)
      setSummary(s)
      setErr(null)
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load STAR Cement telemetry')
    }
    try {
      setAlerts(await alertsPromise)
    } catch {
      // No alerts grant (or a transient failure) -- leave the panel's own previous/empty
      // state rather than blanking the vehicle list/map above.
    }
    try {
      setBreakdowns(await breakdownsPromise)
    } catch {
      // Same reasoning as alerts above, for the breakdown_reports grant.
    }
    setLoaded(true)
    refreshInFlightRef.current = false
  }, [])

  useEffect(() => {
    // 10s, same cadence EIM's LiveView uses -- STAR's Kafka consumer processes
    // messages as they arrive, faster polling here just re-fetches the same snapshot.
    return pollWhileVisible(() => void refresh(), 10_000)
  }, [refresh])

  useEffect(() => {
    // Once, not polled -- the 9 hubs are a seeded reference table (db/star_hub_seed.sql),
    // not live telemetry. Refetching every 10s alongside vehicles would be a wasted round
    // trip for data that only changes on a redeploy.
    fetchStarHubs().then(setHubs).catch(() => setHubs([]))
  }, [])

  useEffect(() => {
    // Once, not polled -- a plant's road network does not change week to week, let alone
    // every 10s.
    fetchStarRoads().then(setRoads).catch(() => setRoads([]))
  }, [])

  useEffect(() => {
    // Once, not polled -- STAR has no external master feeding site_breakdown_code (unlike
    // MBMT's TIMS opr_breakdownmaster sync), so this is empty and the server falls back to
    // mbmt.FALLBACK_CODES; either way it's static for the session.
    fetchStarBreakdownCodes().then(setBreakdownCodes).catch(() => setBreakdownCodes([]))
  }, [])

  const quickResolve = useCallback(async (breakdownId: number) => {
    try {
      await actOnStarBreakdown(breakdownId, 'resolve', { by: 'operator' })
      void refresh()
    } catch {
      // Silent -- the panel's own next 10s poll will reconcile either way, and a failed
      // quick-resolve leaves the breakdown open (safe default: report it back down rather
      // than silently clearing a truck that's still stranded).
    }
  }, [refresh])

  const dismissMoving = useCallback(async (breakdownId: number) => {
    try {
      await dismissStarMoving(breakdownId)
      void refresh()
    } catch {
      // Same silent-and-let-the-next-poll-reconcile reasoning as quickResolve above.
    }
  }, [refresh])

  return (
    <>
      <KpiStrip>
        <KpiCard label="Vehicles reporting" value={summary?.vehicles_reporting ?? '—'} tone="info" dot />
        <KpiCard label="Avg SOC" value={summary?.avg_soc != null ? Math.round(summary.avg_soc) : '—'} unit="%" />
        <KpiCard label="Stationary now" value={summary?.stationary_now ?? '—'} />
        <KpiCard
          label="Alerts today"
          value={summary?.alerts_today ?? '—'}
          tone={summary && summary.alerts_today > 0 ? 'warn' : 'ok'}
          dot
        />
      </KpiStrip>

      <div className="mt-3" ref={mapPanelRef}>
        <StarMap
          vehicles={vehicles} hubs={hubs} roads={roads} focusVehicleId={focusVehicleId}
          onVehicleClick={(id) => setBreakdownFormFor(id)}
        />
      </div>

      {breakdowns.length > 0 && (
        <Panel title={`Breakdowns · ${breakdowns.length}`} className="mt-3">
          <div className="flex flex-col gap-1.5">
            {breakdowns.map((b) => (
              <div key={b.breakdown_id} className="rounded-[10px] border border-[rgba(244,63,94,0.20)] bg-[rgba(244,63,94,0.04)] px-3 py-2">
                <div className="flex items-center gap-3">
                  <AlertOctagon size={13} className="shrink-0 text-[#9F1239]" />
                  <span className="font-mono text-[13px] font-bold tabular-nums text-[#0F172A]">{b.vehicle_id}</span>
                  <span className="text-[10px] font-medium uppercase tracking-wide text-[#9F1239]">{b.category}</span>
                  <span className="text-[11px] text-[#64748B]">{b.note || '—'}</span>
                  <span className="ml-auto text-[11px] text-[#94A3B8]">reported {ago(b.opened_ts)} ago</span>
                  <button
                    type="button"
                    onClick={() => void quickResolve(b.breakdown_id)}
                    className="rounded-full border border-[rgba(15,23,42,0.10)] px-2 py-1 text-[10px] font-semibold text-[#047857] hover:bg-[rgba(16,185,129,0.08)]"
                  >
                    Back in service
                  </button>
                </div>
                {b.resumed_moving_ts != null && (
                  <div className="mt-1.5 flex items-center justify-between gap-2 rounded-md border border-amber-300/60 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800">
                    <span>started moving at {clock(b.resumed_moving_ts)} — resolve?</span>
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        onClick={() => void dismissMoving(b.breakdown_id)}
                        className="rounded px-1.5 py-0.5 text-amber-700 hover:bg-amber-100"
                      >
                        Dismiss
                      </button>
                      <button
                        type="button"
                        onClick={() => void quickResolve(b.breakdown_id)}
                        className="rounded bg-amber-600 px-1.5 py-0.5 font-semibold text-white hover:bg-amber-700"
                      >
                        Resolve
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Panel>
      )}

      <Panel title={`Recent alerts${visibleAlerts.length ? ` · ${visibleAlerts.length}` : ''}`} className="mt-3">
        {visibleAlerts.length === 0 ? (
          <EmptyState title="No alerts" body="Nothing has fired in the last 5 minutes." />
        ) : (
          <div className="flex flex-col gap-1.5">
            {visibleAlerts.map((a) => (
              <div key={a.alert_id} className="flex items-center gap-3 rounded-[10px] border border-[rgba(15,23,42,0.08)] px-3 py-2">
                <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: ALERT_TONE[a.severity] ?? '#64748B' }} />
                <span className="font-mono text-[13px] font-bold tabular-nums text-[#0F172A]">{a.vehicle_id}</span>
                <span className="text-[10px] font-medium uppercase tracking-wide text-[#94A3B8]">{a.kind}</span>
                <span className="ml-auto text-[11px] text-[#64748B]">{a.message}</span>
                <span className="text-[11px] tabular-nums text-[#94A3B8]">
                  {new Date(a.ts).toLocaleTimeString('en-IN', { hour12: false, timeZone: 'Asia/Kolkata' })}
                </span>
                <NetradyneCameraButton site="star_cement" vehicleNumber={a.vehicle_id} alertId={starNetradyneAlertId(a)} />
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel title={`Vehicles${vehicles.length ? ` · ${vehicles.length}` : ''}`} className="mt-3 flex-1">
        {err ? (
          <EmptyState title="Couldn't load STAR Cement telemetry" body={err} />
        ) : !loaded ? (
          <EmptyState title="Loading…" />
        ) : vehicles.length === 0 ? (
          <EmptyState title="No vehicles reporting" body="Either the Kafka consumer hasn't started or no messages have arrived yet." />
        ) : (
          <>
            <div className="mb-2">
              <FilterChips
                options={FLEET_FILTERS.map((f) => ({
                  ...f,
                  count: f.id === 'all'
                    ? vehicles.length
                    : vehicles.filter((v) => starStatus(v, downSet.has(v.vehicle_id), now) === f.id).length,
                }))}
                active={filter}
                onPick={setFilter}
              />
            </div>
            {filteredVehicles.length === 0 ? (
              <EmptyState icon={<AlertOctagon size={26} />} title="No vehicles match" body="Widen the filter to see more." />
            ) : (
              <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-2">
                {filteredVehicles.map((v) => (
                  <StarVehicleTile
                    key={v.vehicle_id}
                    v={v}
                    selected={v.vehicle_id === focusVehicleId}
                    onSelect={() => selectVehicle(v.vehicle_id)}
                    openBreakdown={breakdowns.find((b) => b.vehicle_id === v.vehicle_id) ?? null}
                    onReportBreakdown={() => setBreakdownFormFor(v.vehicle_id)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </Panel>

      {breakdownFormFor && (
        <StarBreakdownForm
          vehicleId={breakdownFormFor}
          existing={breakdowns.find((b) => b.vehicle_id === breakdownFormFor) ?? null}
          codes={breakdownCodes}
          onClose={() => setBreakdownFormFor(null)}
          onSaved={() => void refresh()}
        />
      )}
    </>
  )
}

function fmtDay(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
}

function fmtHours(h: number | null): string {
  return h == null ? '—' : `${h}h`
}

/** Every day-wise breakdown report in one place -- daily counts, what's open right now,
 *  downtime by fault category, repeat offenders, and the data-quality check, all reading
 *  the same f_site_breakdown_* functions the live board's own numbers come from (Phase 2,
 *  db/reports_site_breakdown.sql), so nothing here can disagree with what operators see
 *  on the Live tab. */
function BreakdownReportsPanel(): JSX.Element {
  const [daily, setDaily] = useState<StarBreakdownReportRow<'daily'>[]>([])
  const [open, setOpenRows] = useState<StarBreakdownReportRow<'open'>[]>([])
  const [downtime, setDowntime] = useState<StarBreakdownReportRow<'downtime'>[]>([])
  const [repeat, setRepeat] = useState<StarBreakdownReportRow<'repeat'>[]>([])
  const [hygiene, setHygiene] = useState<StarBreakdownReportRow<'hygiene'>[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    const kinds: StarBreakdownReportKind[] = ['daily', 'open', 'downtime', 'repeat', 'hygiene']
    Promise.all(kinds.map((k) => fetchStarBreakdownReport(k)))
      .then(([d, o, dt, r, h]) => {
        setDaily(d as StarBreakdownReportRow<'daily'>[])
        setOpenRows(o as StarBreakdownReportRow<'open'>[])
        setDowntime(dt as StarBreakdownReportRow<'downtime'>[])
        setRepeat(r as StarBreakdownReportRow<'repeat'>[])
        setHygiene(h as StarBreakdownReportRow<'hygiene'>[])
      })
      .catch(() => { setDaily([]); setOpenRows([]); setDowntime([]); setRepeat([]); setHygiene([]) })
      .finally(() => setLoading(false))
  }, [])

  const h = hygiene[0]

  return (
    <div className="flex flex-1 flex-col gap-3">
      <Panel title="Breakdowns — day by day">
        {loading ? (
          <EmptyState title="Loading…" />
        ) : daily.length === 0 ? (
          <EmptyState title="No breakdowns recorded" body="Nothing has been marked down yet." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="border-b border-[rgba(15,23,42,0.07)] text-left">
                  <th className={EYEBROW + ' py-2 pr-3'}>Day</th>
                  <th className={EYEBROW + ' py-2 pr-3 text-right'}>Opened</th>
                  <th className={EYEBROW + ' py-2 pr-3 text-right'}>Cleared</th>
                  <th className={EYEBROW + ' py-2 pr-3 text-right'}>Vehicles down</th>
                  <th className={EYEBROW + ' py-2 pr-3 text-right'}>Immobilised</th>
                  <th className={EYEBROW + ' py-2 pr-3 text-right'}>Major</th>
                  <th className={EYEBROW + ' py-2 text-right'}>Minor</th>
                </tr>
              </thead>
              <tbody>
                {daily.map((d) => (
                  <tr key={d.day} className="border-b border-[rgba(15,23,42,0.04)] last:border-0">
                    <td className="py-1.5 pr-3 font-mono text-[#0F172A]">{fmtDay(d.day)}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-[#0F172A]">{d.opened}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-[#047857]">{d.cleared}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-[#0F172A]">{d.vehicles_down}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-[#9F1239]">{d.immobilised}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-[#B45309]">{d.major}</td>
                    <td className="py-1.5 text-right font-mono tabular-nums text-[#64748B]">{d.minor}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <div className="grid gap-3 lg:grid-cols-2">
        <Panel title={`Currently down${open.length ? ` · ${open.length}` : ''}`}>
          {open.length === 0 ? (
            <EmptyState title="Nothing down" body="Every truck is in service." />
          ) : (
            <div className="flex flex-col gap-1.5">
              {open.map((o) => (
                <div key={o.breakdown_id} className="rounded-[10px] border border-[rgba(15,23,42,0.08)] px-3 py-2">
                  <div className="flex items-center gap-2 text-[12px]">
                    <span className="font-mono font-bold text-[#0F172A]">{o.vehicle_id}</span>
                    <span className="text-[10px] font-medium uppercase tracking-wide text-[#9F1239]">{o.category}</span>
                    <span className="ml-auto font-mono tabular-nums text-[#64748B]">{fmtHours(o.hours_down)} down</span>
                  </div>
                  {o.eta_overdue && (
                    <div className="mt-1 text-[11px] font-medium text-[#B45309]">ETA has passed</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel title="Repeat offenders">
          {repeat.length === 0 ? (
            <EmptyState title="No repeats" body="No truck has broken down more than once." />
          ) : (
            <div className="flex flex-col gap-1.5">
              {repeat.map((r) => (
                <div key={r.vehicle_id} className="flex items-center gap-2 rounded-[10px] border border-[rgba(15,23,42,0.08)] px-3 py-2 text-[12px]">
                  <span className="font-mono font-bold text-[#0F172A]">{r.vehicle_id}</span>
                  <span className="text-[#64748B]">{r.incidents} incidents</span>
                  <span className="ml-auto text-[11px] text-[#94A3B8]">{r.most_common_cause ?? '—'}</span>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      <Panel title="Downtime by fault category">
        {downtime.length === 0 ? (
          <EmptyState title="No closed incidents yet" body="Downtime can only be measured once a breakdown clears." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="border-b border-[rgba(15,23,42,0.07)] text-left">
                  <th className={EYEBROW + ' py-2 pr-3'}>Category</th>
                  <th className={EYEBROW + ' py-2 pr-3'}>Severity</th>
                  <th className={EYEBROW + ' py-2 pr-3 text-right'}>Incidents</th>
                  <th className={EYEBROW + ' py-2 pr-3 text-right'}>Median</th>
                  <th className={EYEBROW + ' py-2 text-right'}>Total lost</th>
                </tr>
              </thead>
              <tbody>
                {downtime.map((d, i) => (
                  <tr key={`${d.category}-${d.severity}-${i}`} className="border-b border-[rgba(15,23,42,0.04)] last:border-0">
                    <td className="py-1.5 pr-3 text-[#0F172A]">{d.category}</td>
                    <td className="py-1.5 pr-3 text-[#64748B]">{d.severity}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-[#0F172A]">{d.incidents}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-[#64748B]">{fmtHours(d.median_hours)}</td>
                    <td className="py-1.5 text-right font-mono tabular-nums text-[#0F172A]">{fmtHours(d.total_hours_lost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {h && (
        <Panel title="Record quality">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div>
              <div className="font-mono text-[20px] font-bold tabular-nums text-[#0F172A]">{h.incidents_logged}</div>
              <div className={EYEBROW}>logged</div>
            </div>
            <div>
              <div className="font-mono text-[20px] font-bold tabular-nums text-[#0F172A]">{h.never_cleared}</div>
              <div className={EYEBROW}>never cleared</div>
            </div>
            <div>
              <div className="font-mono text-[20px] font-bold tabular-nums text-[#B45309]">{h.open_over_7_days}</div>
              <div className={EYEBROW}>open 7d+</div>
            </div>
            <div>
              <div className="font-mono text-[20px] font-bold tabular-nums text-[#0F172A]">
                {h.never_cleared_pct != null ? `${h.never_cleared_pct}%` : '—'}
              </div>
              <div className={EYEBROW}>never-cleared rate</div>
            </div>
          </div>
        </Panel>
      )}
    </div>
  )
}

function AlertsView(): JSX.Element {
  const [alerts, setAlerts] = useState<StarAlert[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    fetchStarAlerts(undefined, 200).then(setAlerts).catch(() => setAlerts([])).finally(() => setLoading(false))
  }, [])

  return (
    <Panel title="Alerts — last 200">
      {loading ? (
        <EmptyState title="Loading…" />
      ) : alerts.length === 0 ? (
        <EmptyState title="No alerts yet" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="border-b border-[rgba(15,23,42,0.07)] text-left">
                <th className={EYEBROW + ' py-2 pr-3'}>Vehicle</th>
                <th className={EYEBROW + ' py-2 pr-3'}>Kind</th>
                <th className={EYEBROW + ' py-2 pr-3'}>Severity</th>
                <th className={EYEBROW + ' py-2 pr-3'}>Message</th>
                <th className={EYEBROW + ' py-2 text-right'}>Time</th>
              </tr>
            </thead>
            <tbody>
              {alerts.map((a) => (
                <tr key={a.alert_id} className="border-b border-[rgba(15,23,42,0.04)] last:border-0">
                  <td className="py-1.5 pr-3 font-mono font-semibold tabular-nums text-[#0F172A]">{a.vehicle_id}</td>
                  <td className="py-1.5 pr-3 text-[#64748B]">{a.kind}</td>
                  <td className="py-1.5 pr-3" style={{ color: ALERT_TONE[a.severity] ?? '#64748B' }}>{a.severity}</td>
                  <td className="py-1.5 pr-3 text-[#64748B]">{a.message}</td>
                  <td className="py-1.5 text-right font-mono text-[11px] tabular-nums text-[#94A3B8]">
                    {new Date(a.ts).toLocaleTimeString('en-IN', { hour12: false, timeZone: 'Asia/Kolkata' })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  )
}

function ReportsView(): JSX.Element {
  return (
    <div className="flex flex-1 flex-col gap-3">
      <BreakdownReportsPanel />
      <AlertsView />
    </div>
  )
}

/**
 * STAR Cement (PROPEL vendor, Meghalaya, 5 BEV trucks). No TIMS register, no drawn
 * corridor -- reads straight off star_vehicle_telemetry/star_alert/star_speed_profile
 * (db/schema_star.sql) via serving/app.py's /api/star/* endpoints. The live map
 * (StarMap.tsx) shows vehicle positions and the 9 KML-derived hub points -- no route/
 * corridor geometry exists to draw a line on, which is a different thing from having no
 * map at all (EIM has the same no-corridor shape and shows a live map for exactly this
 * reason).
 */
export function StarShell(): JSX.Element {
  const navigate = useNavigate()
  const { tab } = useParams()
  const view = tab === 'reports' ? 'reports' : 'live'
  const session = useSession()
  const userLabel = sessionDisplayName(session)
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])

  return (
    <div className="flex h-full flex-col overflow-hidden bg-[#E9EEF5]">
      <div className="shrink-0 px-3 pt-3">
        <TopBar
          clock={new Date(now).toLocaleTimeString('en-IN', { hour12: false, timeZone: 'Asia/Kolkata' })}
          live
          user={userLabel}
          fleets={fleetPills(true)}
          activeFleet="star"
          onFleet={(id) => navigate(pathFor(id as FleetId, defaultSurfaceFor(id as FleetId)))}
          nav={surfacePills('star')}
          activeNav={view}
          onNav={(id) => navigate(pathFor('star', id === 'reports' ? 'reports' : 'live'))}
          onSettings={() => navigate('/settings')}
        />
      </div>

      <div className="shrink-0 flex items-end justify-between gap-3 px-3 pt-3">
        <div>
          <p className={EYEBROW}>Meghalaya · PROPEL</p>
          <h1 className="font-display text-[22px] font-semibold tracking-tight text-[#0F172A]">
            STAR Cement
          </h1>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {view === 'live' ? <LiveView /> : <ReportsView />}
      </div>
    </div>
  )
}
